import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';
import SoundEffects from '../utils/system/SoundEffects';

/**
 * Reusable Modal Component
 * Provides consistent modal behavior across the app
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal closes
 * @param {string} title - Modal title (optional)
 * @param {ReactNode} children - Modal content
 * @param {string} size - Modal size: 'small', 'medium', 'large', 'full' (default: 'medium')
 * @param {boolean} closeOnBackdrop - Close when clicking backdrop (default: true)
 * @param {boolean} closeOnEscape - Close when pressing Escape (default: true)
 * @param {boolean} showCloseButton - Show X close button (default: true)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles for modal content
 * @param {boolean} blurBackdrop - Apply backdrop blur effect (default: false)
 * @param {string} backdropColor - Custom backdrop color (default: rgba(0, 0, 0, 0.85))
 * @param {ReactNode} footer - Optional footer content
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  style = {},
  blurBackdrop = false,
  backdropColor,
  footer
}) => {
  // Handle Escape key
  const handleEscape = useCallback((e) => {
    if (closeOnEscape && e.key === 'Escape') {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  // Add/remove escape key listener
  useEffect(() => {
    if (isOpen && closeOnEscape) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, handleEscape]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Play modal open sound
      SoundEffects.play('modal_open', { volume: 0.2 });
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleClose = () => {
    // Play modal close sound
    SoundEffects.play('modal_close', { volume: 0.2 });
    onClose();
  };

  const modalClasses = [
    'modal-content',
    `modal-${size}`,
    className
  ].filter(Boolean).join(' ');

  const overlayStyle = {
    ...(backdropColor && { background: backdropColor })
  };

  return createPortal(
    <div 
      className={`modal-overlay ${blurBackdrop ? 'modal-blur' : ''}`}
      onClick={handleBackdropClick}
      style={overlayStyle}
    >
      <div 
        className={modalClasses}
        onClick={(e) => e.stopPropagation()}
        style={style}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <div className="modal-title">{title}</div>}
            {showCloseButton && (
              <button 
                className="modal-close-button"
                onClick={handleClose}
                aria-label="Close modal"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
